'use client';

import type { CreatedApiKey } from '@interfaces/apiKey';
import styles from '@styles/ApiKeys/ApiKeys.module.scss';
import { Alert, Button, Checkbox, Modal, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { formatDate } from './formatDate';

/**
 * The one and only time the plaintext key exists in the browser.
 *
 * The API stores a hash, so a key that leaves this dialog uncopied is
 * unrecoverable — which is why the dialog cannot be dismissed by the mask, the
 * escape key or a close icon. The acknowledgement is the only way out, and it
 * is deliberately a decision rather than a reflex.
 */

type Props = {
  createdKey: CreatedApiKey | null;
  onDismiss: () => void;
};

const CreatedKeyModal: React.FC<Props> = ({ createdKey, onDismiss }) => {
  const [hasAcknowledged, setHasAcknowledged] = useState(false);

  // Reset per key, so acknowledging one key doesn't pre-acknowledge the next.
  useEffect(() => {
    if (createdKey !== null) {
      setHasAcknowledged(false);
    }
  }, [createdKey]);

  const expiresAt = formatDate(createdKey?.expiresAt);

  return (
    <Modal
      open={createdKey !== null}
      title="Copy your API key"
      closable={false}
      maskClosable={false}
      keyboard={false}
      width={620}
      footer={
        <Button color="primary" variant="solid" disabled={!hasAcknowledged} onClick={onDismiss}>
          Done
        </Button>
      }
    >
      {createdKey !== null && (
        <div className={styles.createdKey}>
          <Alert
            type="warning"
            showIcon
            title="This key is shown once"
            description="We store a hash of the key, not the key itself, so we cannot show it to you again or recover it for you. Store it in your deployment's secret manager now. If you lose it, revoke the key and create another."
          />

          <div className={styles.createdKey__value}>
            <Typography.Paragraph
              className={styles.createdKey__text}
              copyable={{ text: createdKey.key, tooltips: ['Copy key', 'Copied'] }}
            >
              {createdKey.key}
            </Typography.Paragraph>
          </div>

          <div className={styles.createdKey__meta}>
            <span>
              Name: <strong>{createdKey.name}</strong>
            </span>
            <span>Expires: {expiresAt ?? 'Never'}</span>
          </div>

          <div className={styles.createdKey__acknowledge}>
            <Checkbox checked={hasAcknowledged} onChange={(event) => setHasAcknowledged(event.target.checked)}>
              I have copied this key and understand it will not be shown again.
            </Checkbox>
          </div>
        </div>
      )}
    </Modal>
  );
};

export { CreatedKeyModal };
